/**
 * ProtocolsManagementModal - Centralized management of protocols in a modal.
 *
 * Navigation Pattern:
 * Uses in-place view transitions (not nested modals) for create/edit flows.
 * Views animate with sequenced fade-out → resize → fade-in for smooth transitions.
 *
 * @see ./protocols-list-view.tsx - List view component
 * @see ./protocols-form-view.tsx - Create/Edit form view component
 */

import type { SuggestedChip } from '@/components/ui/transcript-chips/types';
import { useEvidenceBillingInfo } from '@/hooks/useEvidenceBillingInfo';
import { Dialog, DialogContent } from '@heidi/design-system/dialog';
import { AnimatePresence } from 'motion/react';
import * as m from 'motion/react-m';
import { memo, useCallback, useEffect, useState } from 'react';
import { ProtocolsFormView } from './protocols-form-view';
import { ProtocolsListView } from './protocols-list-view';

export type PinnedProtocol = SuggestedChip & {
  isDefault?: boolean;
  teamShortcutId?: string;
};

/** View mode for the modal */
type ViewMode = 'list' | 'create' | 'edit';

/** Animation easing: ease-out-quint for smooth deceleration */
const EASE_OUT_QUINT = [0.23, 1, 0.32, 1] as const;

/** Animation duration for content fade transitions (seconds) */
const CONTENT_FADE_DURATION = 0.15;

export interface ProtocolsManagementModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  protocols: PinnedProtocol[];
  onRemoveProtocol: (chipId: string) => void;
  onAddProtocol: (label: string, query: string) => void;
  onEditProtocol?: (chipId: string, label: string, query: string) => void;
  onBrowseLibrary?: () => void;
  shortcutCount?: number;
  maxShortcuts?: number;
  isAtShortcutLimit?: boolean;
}

export const ProtocolsManagementModal = memo(
  ({
    open,
    onOpenChange,
    protocols,
    onRemoveProtocol,
    onAddProtocol,
    onEditProtocol,
    onBrowseLibrary,
    shortcutCount = 0,
    maxShortcuts = 25,
    isAtShortcutLimit = false,
  }: ProtocolsManagementModalProps) => {
    const [viewMode, setViewMode] = useState<ViewMode>('list');
    const [selectedProtocol, setSelectedProtocol] = useState<PinnedProtocol | null>(null);
    const [editLabel, setEditLabel] = useState('');
    const [editQuery, setEditQuery] = useState('');

    const { hasEvidencePro, isInFreeTrial } = useEvidenceBillingInfo();
    const showProBranding = !(hasEvidencePro && !isInFreeTrial);

    const resetState = useCallback(() => {
      setViewMode('list');
      setSelectedProtocol(null);
      setEditLabel('');
      setEditQuery('');
    }, []);

    const handleOpenChange = useCallback(
      (isOpen: boolean) => {
        if (!isOpen) resetState();
        onOpenChange(isOpen);
      },
      [onOpenChange, resetState]
    );

    useEffect(() => {
      if (!open) resetState();
    }, [open, resetState]);

    const handleOpenCreate = useCallback(() => {
      if (isAtShortcutLimit) return;
      setSelectedProtocol(null);
      setEditLabel('');
      setEditQuery('');
      setViewMode('create');
    }, [isAtShortcutLimit]);

    const handleOpenEdit = useCallback((protocol: PinnedProtocol) => {
      setSelectedProtocol(protocol);
      setEditLabel(protocol.label);
      setEditQuery(protocol.query);
      setViewMode('edit');
    }, []);

    const handleBack = useCallback(() => resetState(), [resetState]);

    const handleSave = useCallback(() => {
      if (!editLabel.trim() || !editQuery.trim()) return;
      if (viewMode === 'create') {
        onAddProtocol(editLabel.trim(), editQuery.trim());
      } else if (viewMode === 'edit' && selectedProtocol) {
        onEditProtocol?.(selectedProtocol.id, editLabel.trim(), editQuery.trim());
      }
      handleBack();
    }, [
      viewMode,
      editLabel,
      editQuery,
      selectedProtocol,
      onAddProtocol,
      onEditProtocol,
      handleBack,
    ]);

    const handleDelete = useCallback(() => {
      if (selectedProtocol) onRemoveProtocol(selectedProtocol.id);
      handleBack();
    }, [selectedProtocol, onRemoveProtocol, handleBack]);

    const handleBrowseLibrary = useCallback(() => {
      onOpenChange(false);
      onBrowseLibrary?.();
    }, [onOpenChange, onBrowseLibrary]);

    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          hideCloseIcon
          className="flex max-h-[70dvh] flex-col gap-0 overflow-hidden bg-surface-page p-6 sm:max-h-150 sm:max-w-[520px] sm:rounded-xl"
        >
          <AnimatePresence mode="wait" initial={false}>
            {viewMode === 'list' ? (
              <m.div
                key="list"
                className="flex min-h-0 flex-1 flex-col gap-6"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: CONTENT_FADE_DURATION, ease: EASE_OUT_QUINT }}
              >
                <ProtocolsListView
                  protocols={protocols}
                  showProBranding={showProBranding}
                  onOpenCreate={handleOpenCreate}
                  onOpenEdit={handleOpenEdit}
                  onRemoveProtocol={onRemoveProtocol}
                  onBrowseLibrary={onBrowseLibrary ? handleBrowseLibrary : undefined}
                  shortcutCount={shortcutCount}
                  maxShortcuts={maxShortcuts}
                  isAtShortcutLimit={isAtShortcutLimit}
                />
              </m.div>
            ) : (
              <m.div
                key="form"
                className="flex min-h-0 flex-1 flex-col gap-6"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: CONTENT_FADE_DURATION, ease: EASE_OUT_QUINT }}
              >
                <ProtocolsFormView
                  mode={viewMode === 'create' ? 'create' : 'edit'}
                  protocolLabel={selectedProtocol?.label}
                  editLabel={editLabel}
                  editQuery={editQuery}
                  onLabelChange={setEditLabel}
                  onQueryChange={setEditQuery}
                  onBack={handleBack}
                  onSave={handleSave}
                  onDelete={viewMode === 'edit' ? handleDelete : undefined}
                />
              </m.div>
            )}
          </AnimatePresence>
        </DialogContent>
      </Dialog>
    );
  }
);

ProtocolsManagementModal.displayName = 'ProtocolsManagementModal';

export default ProtocolsManagementModal;
